'use client'

import { toast as heroToast } from '@heroui/react'

const TOAST_COLORS = ['default', 'primary', 'secondary', 'success', 'warning', 'danger'] as const
type ToastColor = typeof TOAST_COLORS[number]

/**
 * Predicate-based suppression helpers.
 *
 * Use via `showToast({ ..., skipIf: ToastSuppress.onMobile })` when a toast
 * would be redundant with another UI affordance in a given context
 * (e.g. "Added to cart" is redundant when the cart drawer opens on mobile).
 *
 * Add new predicates here as new scenarios come up — this keeps suppression
 * rules centralized and prevents ad-hoc `if (isMobile) return` sprinkled
 * across the codebase.
 */
export const ToastSuppress = {
  /** Mobile viewport (<768px). */
  onMobile: () => typeof window !== 'undefined' && window.innerWidth < 768,
  /** Desktop viewport (≥768px). */
  onDesktop: () => typeof window !== 'undefined' && window.innerWidth >= 768,
  /** Always suppress (useful for temporarily disabling a toast). */
  always: () => true,
  /** Never suppress (default behavior). */
  never: () => false
}

export interface ToastOptions {
  title: string
  description?: string
  color?: ToastColor
  timeout?: number
  /**
   * Suppress this toast when the predicate returns true.
   * Use `ToastSuppress.*` helpers for common scenarios, or pass a custom fn.
   * Example: `skipIf: ToastSuppress.onMobile` hides the toast on mobile.
   */
  skipIf?: () => boolean
}

const variantMap: Record<string, string> = {
  primary: 'accent',
  secondary: 'default',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  default: 'default',
}

export function showToast(options: ToastOptions | string) {
  if (typeof options === 'string') {
    return heroToast(options, { variant: 'success' as any })
  }

  // Suppression check — bail before queuing the toast
  if (options.skipIf?.()) {
    return
  }

  const variant = variantMap[options.color || 'default'] || 'default'
  return heroToast(options.title, {
    description: options.description,
    variant: variant as any,
    timeout: options.timeout ?? 5000,
  })
}

export const toast = (options: { title: string; color?: ToastColor; timeout?: number }) => {
  showToast(options)
}
